<template>
  <div class="leaderboard">

    <div class="lb-header">
      <div class="lb-header-left">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#00b4b4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 19h20M3 9l4 4 5-7 5 7 4-4v10H3z"/>
        </svg>
        <h2>FreeSpan Opportunities Leaderboard</h2>
      </div>
      <a href="/freespan-opportunity-registration" class="submit-btn">
        Submit Opportunity <i class="material-icons">keyboard_arrow_right</i>
      </a>
    </div>

    <div v-if="loading" class="loading">
      <AtomSpinner :animation-duration="1000" :size="60" color="#00b4b4" />
    </div>

    <div v-else class="lb-rows">

      <div
        v-for="(entry, index) in flatEntries"
        :key="index"
        class="lb-row"
        
      >
        <div class="rank-num">{{ entry.rank }}</div>
        <div class="info">
          <p class="entry-name">
            {{ entry.SalesRep }}
            <span class="entry-co"> — {{ formatCompanyName(entry.AreaContractorName) }}</span>
          </p>
          <div class="bar-wrap">
            <div
              class="bar"
              :style="{ width: barWidth(entry.SubmissionCount) + '%' }"
            ></div>
          </div>
        </div>
        <span class="subs">{{ entry.SubmissionCount }} submission{{ entry.SubmissionCount !== 1 ? 's' : '' }}</span>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";
import { AtomSpinner } from "epic-spinners";

export default {
  name: 'FreespanLeaderboard',
  components: { AtomSpinner },
  data() {
    return {
      server: import.meta.env.VITE_APP_SERVER_URL,
      loading: true,
      rawSubmissions: [],
      leaderboard: null
    };
  },
  created() {
    this.fetchLeaderboard();
  },
  computed: {
    flatEntries() {
      if (!this.leaderboard) return [];
      const result = [];
      const places = [
        { key: 'firstPlace',  rank: 1 },
        { key: 'secondPlace', rank: 2 },
        { key: 'thirdPlace',  rank: 3 }
      ];
      for (const { key, rank } of places) {
        for (const entry of this.leaderboard[key] || []) {
          result.push({ ...entry, rank });
        }
      }
      return result;
    },
    maxCount() {
      if (!this.flatEntries.length) return 1;
      return Math.max(...this.flatEntries.map(e => e.SubmissionCount));
    }
  },
  methods: {
    async fetchLeaderboard() {
      const auth = {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("USER"),
        },
      };
      try {
        const response = await axios.get(`${this.server}/api/v1/freespan-stats`, auth);
        this.rawSubmissions = response.data;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    formatCompanyName(name) {
      if (!name) return '';
      return name
        .toLowerCase()
        .split(/\s+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    barWidth(count) {
      return Math.round((count / this.maxCount) * 100);
    },
    barOpacity(rank) {
      if (rank === 1) return 1;
      if (rank === 2) return 0.55;
      return 0.35;
    },
    createLeaderboard(data) {
      if (!Array.isArray(data) || data.length === 0) {
        return { firstPlace: [], secondPlace: [], thirdPlace: [] };
      }
      const sorted = [...data].sort((a, b) => b.SubmissionCount - a.SubmissionCount);
      const result = { firstPlace: [], secondPlace: [], thirdPlace: [] };
      let currentRank = 1;
      let i = 0;
      while (i < sorted.length && currentRank <= 3) {
        const currentCount = sorted[i].SubmissionCount;
        const group = [];
        while (i < sorted.length && sorted[i].SubmissionCount === currentCount) {
          group.push({
            SalesRep: sorted[i].SalesRep,
            AreaContractorName: sorted[i].AreaContractorName,
            SubmissionCount: sorted[i].SubmissionCount
          });
          i++;
        }
        if (currentRank === 1) result.firstPlace = group;
        else if (currentRank === 2) result.secondPlace = group;
        else if (currentRank === 3) result.thirdPlace = group;
        currentRank++;
      }
      return result;
    }
  },
  watch: {
    rawSubmissions: {
      handler(newData) {
        this.leaderboard = this.createLeaderboard(newData);
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.leaderboard {
  max-width: 1200px;
  margin: 20px 0;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

/* Header */
.lb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 16px;
}

.lb-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.lb-header-left h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 500;
  color: #111;
}

.submit-btn {
  font-size: 12px;
  color: #00b4b4;
  border: 1px solid #00b4b4;
  border-radius: 4px;
  padding: 5px 12px;
  display: flex;
  align-items: center;
  gap: 2px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.25s ease;
}

.submit-btn:hover {
  background: #00b4b4;
  color: #fff;
}

/* Loading */
.loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

/* Rows */
.lb-rows {
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
}

.lb-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
}

.lb-row:last-child {
  border-bottom: none;
}

/* Rank number */
.rank-num {
  font-size: 22px;
  font-weight: 700;
  color: #ccced1;
  width: 28px;
  text-align: center;
  flex-shrink: 0;
  line-height: 1;
}

.lb-row.first .rank-num {
  color: #00b4b4;
}

/* Info: name + bar */
.info {
  flex: 1;
  min-width: 0;
}

.entry-name {
  font-size: 13px;
  font-weight: 500;
  color: #111;
  margin: 0 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.entry-co {
  font-weight: 400;
  color: #6c757d;
}

.bar-wrap {
  height: 4px;
  background: #f3f4f6;
  border-radius: 2px;
  overflow: hidden;
}

.bar {
  height: 4px;
  background: #00b4b4;
  border-radius: 2px;
  transition: width 0.4s ease;
}

/* Submission count */
.subs {
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
  white-space: nowrap;
}

.lb-row.first .subs {
  color: #00b4b4;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 560px) {
  .subs {
    display: none;
  }
}
</style>